import React, { useEffect } from "react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import SEOHead from "../components/common/SEOHead";
import AllProjects from "../components/projects/allProjects";

import SEO from "../data/seo";

import "./styles/projects.css";

const impactMetrics = [
	{ value: "68%", label: "Return increase", note: "Early signal from customers who would otherwise lose physical cards" },
	{ value: "32%", label: "Sales growth", note: "Recurring sales growth observed across active usage" },
	{ value: "100%", label: "Less paper", note: "Paper and plastic loyalty cards replaced digitally" },
	{ value: "3.5x", label: "More efficient", note: "Less time spent managing loyalty activity" },
];

const successCases = [
	{
		name: "Tazze Pazze",
		logo: "/swapstamp/logo-tazze-pazze.png",
		vendorId: "3a7e1153-4288-4243-a17a-fcd50839e41f",
		joinCard: "108",
		givePoint: "1,489",
		rewards: "154",
	},
	{
		name: "The Habitat Club",
		logo: "/swapstamp/logo-habitat-club.png",
		vendorId: "68b74519-2811-45a9-91f5-7c42a6b58b28",
		joinCard: "188",
		givePoint: "1,293",
		rewards: "81",
	},
];

const howItWorks = [
	{
		title: "Add the business",
		text: "Name, address and logo. A vendor profile can be ready in seconds.",
	},
	{
		title: "Design the first card",
		text: "Choose color, stamp type, total stamps and reward. The merchant controls the loyalty rule.",
	},
	{
		title: "Go live",
		text: "Sign in with Google or email and start giving the first stamp from the dashboard.",
	},
];

const landingLinks = [
	{
		title: "Coffee shops landing",
		text: "For cafes, bakeries and counter-service coffee businesses.",
		href: "/swapstamp/landing/coffeshop/en",
	},
	{
		title: "Barbershop landing",
		text: "For barber shops, salons and recurring appointment businesses.",
		href: "/swapstamp/landing/barber/en",
	},
	{
		title: "Ice cream shop landing",
		text: "For gelaterie, dessert shops and seasonal repeat visits.",
		href: "/swapstamp/landing/icecream/en",
	},
];

const businessTypes = [
	"Cafes and coffee shops",
	"Barbershops",
	"Beauty salons and spas",
	"Restaurants and bars",
	"Retail stores",
	"Bakeries and pastry shops",
	"Nail salons, gyms, laundromats, pet stores and florists",
];

const Projects = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "projects");

	return (
		<React.Fragment>
			<SEOHead
				title="Swapstamp Digital Loyalty Programs"
				description={currentSEO.description}
				keywords={currentSEO.keywords}
				path="/swapstamp"
				image="/swapstamp/swapstamp.png"
				jsonLd={[
					{
						"@context": "https://schema.org",
						"@type": "SoftwareApplication",
						"@id": "https://www.northwestwind.org/swapstamp#software",
						name: "Swapstamp",
						applicationCategory: "BusinessApplication",
						operatingSystem: "Web, iOS Wallet, Android Wallet",
						url: "https://app.swapstamp.com/hub/onboarding",
						description:
							"Digital loyalty card software for local businesses using QR cards, dashboard controls, rewards, feedback and wallet integrations.",
						creator: { "@id": "https://www.northwestwind.org/#person" },
					},
					{
						"@context": "https://schema.org",
						"@type": "WebPage",
						"@id": "https://www.northwestwind.org/swapstamp#webpage",
						url: "https://www.northwestwind.org/swapstamp",
						name: "Swapstamp Digital Loyalty Programs",
						description: currentSEO.description,
						mainEntity: { "@id": "https://www.northwestwind.org/swapstamp#software" },
					},
				]}
			/>

			<div className="page-content">
				<NavBar active="swapstamp" />
				<div className="content-wrapper">
					<div className="projects-logo-container">
						<div className="projects-logo">
							<Logo width={46} />
						</div>
					</div>
					<div className="projects-container">
						<div className="projects-hero">
							<div className="projects-hero-copy">
								<div className="title projects-title">Swapstamp</div>

								<div className="projects-brand-link">
									<a href="https://app.swapstamp.com/hub/onboarding?source=northwestwind" target="_blank" rel="noreferrer">
										<div className="projects-brand-link-icon">
											<img src="/swapstamp.svg" alt="Swapstamp" className="projects-brand-link-image" />
										</div>
										<div className="projects-brand-link-text">Digital Loyalty Programs</div>
									</a>
								</div>

								<div className="subtitle projects-subtitle">
									<p>Swapstamp is a digital loyalty card system for local businesses that want repeat customers without paper cards, customer app downloads or complex setup.</p>
									<p>It is built around a simple QR flow: merchants create a card, customers join from their phone, and every stamp or reward is tracked from a live dashboard.</p>
									<p>The current product combines vendor workflows, customer flows, Cognito authentication, Lambda APIs, wallet integrations and real usage from active merchants.</p>
								</div>
							</div>

							<div className="projects-video-box">
								<div className="hero-video-grid">
									<div className="demo-phone hero-demo-phone">
										<video src="/videos/NORTHWESTWIND_Swapstamp_1.mp4" autoPlay muted loop playsInline preload="auto" />
									</div>
									<div className="demo-phone hero-demo-phone hero-demo-phone-offset">
										<video src="/videos/NORTWESTWIND_Swapstamp2.mp4" autoPlay muted loop playsInline preload="auto" />
									</div>
								</div>
							</div>
						</div>

						<section className="swapstamp-section landing-link-section">
							<div className="swapstamp-section-header">
								<div>
									<div className="swapstamp-kicker">Conversion landing pages</div>
									<h2>Three focused landing funnels</h2>
								</div>
								<p>
									The portfolio view stays here. The commercial pages open separately and are built for search traffic, hot leads and onboarding conversion.
								</p>
							</div>
							<div className="landing-link-grid">
								{landingLinks.map((link) => (
									<a className="landing-link-card" href={link.href} target="_blank" rel="noreferrer" key={link.href}>
										<strong>{link.title}</strong>
										<span>{link.text}</span>
									</a>
								))}
							</div>
						</section>

						<section className="swapstamp-section impact-section">
							<div className="swapstamp-section-header">
								<div>
									<div className="swapstamp-kicker">Real Impact</div>
									<h2>Results that make a difference</h2>
								</div>
								<p>
									Live usage signals from early merchants show where Swapstamp is useful:
									reducing lost cards, increasing recurring visits and cutting manual loyalty work.
								</p>
							</div>
							<div className="impact-grid">
								{impactMetrics.map((metric) => (
									<div className="impact-card" key={metric.label}>
										<div className="impact-value">{metric.value}</div>
										<div className="impact-label">{metric.label}</div>
										<div className="impact-note">{metric.note}</div>
									</div>
								))}
							</div>
						</section>

						<section className="swapstamp-section case-section">
							<div className="swapstamp-section-header">
								<div>
									<div className="swapstamp-kicker">Active Vendors</div>
									<h2>Two live success cases</h2>
								</div>
								<p>
									These are not mockups. They are active vendor records with real joins,
									stamps and completed rewards moving through the product.
								</p>
							</div>
							<div className="case-grid">
								{successCases.map((vendor) => (
									<div className="case-card" key={vendor.vendorId}>
										<div className="case-card-top">
											<div className="case-logo-wrap">
												<img src={vendor.logo} alt={`${vendor.name} logo`} />
											</div>
											<div>
												<h3>{vendor.name}</h3>
												<div className="case-vendor-id">{vendor.vendorId}</div>
											</div>
										</div>
										<div className="case-stats">
											<div>
												<strong>{vendor.joinCard}</strong>
												<span>Join card</span>
											</div>
											<div>
												<strong>{vendor.givePoint}</strong>
												<span>Give point</span>
											</div>
											<div>
												<strong>{vendor.rewards}</strong>
												<span>Rewards completed</span>
											</div>
										</div>
									</div>
								))}
							</div>
						</section>

						<section className="swapstamp-section how-section">
							<div className="swapstamp-section-header">
								<div>
									<div className="swapstamp-kicker">How It Works</div>
									<h2>Set up a program in minutes</h2>
								</div>
								<p>
									No sales calls, no technical configuration and no customer app download.
									The merchant can create the first loyalty card and start stamping quickly.
								</p>
							</div>
							<div className="how-grid">
								{howItWorks.map((step, index) => (
									<div className="how-card" key={step.title}>
										<div className="how-number">{index + 1}</div>
										<h3>{step.title}</h3>
										<p>{step.text}</p>
									</div>
								))}
							</div>
							<div className="how-live-note">Sign in with Google or email. Live in 30 seconds.</div>
						</section>

						<section className="swapstamp-section fit-section">
							<div className="fit-copy">
								<div className="swapstamp-kicker">For Local Businesses</div>
								<h2>Who is Swapstamp for?</h2>
								<p>
									Swapstamp fits local businesses with recurring customers: places where
									a simple reward can turn a casual visit into a habit.
								</p>
							</div>
							<div className="business-type-grid">
								{businessTypes.map((type) => (
									<div className="business-type" key={type}>{type}</div>
								))}
							</div>
						</section>

						<section className="swapstamp-section technical-section">
							<div className="swapstamp-section-header platform-header">
								<div>
									<div className="swapstamp-kicker">Technical blog</div>
									<h2>Engineering notes behind Swapstamp</h2>
								</div>
								<p>
									These articles break Swapstamp into concrete technical topics:
									infrastructure, authentication, backend services, vendor logic,
									customer flows and wallet integrations.
								</p>
							</div>
							<div className="projects-list">
								<AllProjects />
							</div>
						</section>
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Projects;
